import { useQuery } from '@apollo/client';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { DelayedSkeleton, DetailsWithListsSkeleton } from '@/components/ui/delayed-skeleton';

import { CONTRACT_DETAIL_QUERY } from './contracts.gql';
import { ContractDetails } from './contract-details';
import { ContractListItem } from './types';
import { computeContractStatus } from './utils';

interface ContractDetailsQueryProps {
        contractId: string;
        showHeader?: boolean;
        onEdit?: (id: string) => void;
        onClone?: (id: string) => void;
        onDelete?: (id: string) => void;
        onClose?: () => void;
        onBack?: () => void;
        showBackButton?: boolean;
}

interface ContractDetailNode {
        contractId: string;
        contractNumber?: string | null;
        beginDate: string;
        endDate: string;
        amount?: number | null;
        isAutoRenewed: boolean;
        simultaneousVehicleLimit?: number | null;
        isActive: boolean;
        customer?: {
                customerId: string;
                firmName?: string | null;
                firstName?: string | null;
                lastName?: string | null;
                isCompany?: boolean | null;
                customerCategory?: {
                        description?: string | null;
                        simultaneousVehicleLimit?: number | null;
                } | null;

        } | null;
        contractVehicles?: Array<{
                contractVehicleId: string;
                vehicleId: string;
                rfidTag?: string | null;
                isVip: boolean;
                vehicle?: {
                        vehicleId: string;
                        plateNumber?: string | null;
                        vehicleType?: {
                                name?: string | null;
                        } | null;
                        make?: string | null;
                        model?: string | null;
                        color?: string | null;
                } | null;
        }> | null;
        parkingSessions?: Array<{
                parkingSessionId: string;
                entryDateTime: string;
                exitDateTime?: string | null;
                isInside: boolean;
        }> | null;
}

interface ContractDetailQueryResponse {
        contract?: ContractDetailNode | null;
}

const mapNodeToListItem = (node: ContractDetailNode): ContractListItem => {
        return {
                contractId: node.contractId,
                contractNumber: node.contractNumber ?? null,
                customer: {
                        customerId: node.customer?.customerId ?? 'unknown-customer',
                        firmName: node.customer?.firmName ?? null,
                        firstName: node.customer?.firstName ?? null,
                        lastName: node.customer?.lastName ?? null,
                        isCompany: node.customer?.isCompany ?? null,
                        categoryName: node.customer?.customerCategory?.description ?? undefined,
                        categoryLimit: node.customer?.customerCategory?.simultaneousVehicleLimit ?? undefined,
                        contactName: undefined,
                        contactEmail: undefined,
                        contactPhone: undefined,
                },
                beginDate: node.beginDate,
                endDate: node.endDate,
                amount: (() => {
                        if (typeof node.amount === 'number') {
                                return node.amount;
                        }
                        if (typeof node.amount === 'string') {
                                const parsed = Number(node.amount);
                                return Number.isNaN(parsed) ? null : parsed;
                        }
                        return null;
                })(),
                billingCycle: undefined, // Not available in current schema
                isAutoRenewed: node.isAutoRenewed,
                simultaneousVehicleLimit: node.simultaneousVehicleLimit ?? undefined,
                isActive: node.isActive,
                contractVehicles: node.contractVehicles?.map(vehicle => ({
                        contractVehicleId: vehicle.contractVehicleId,
                        vehicleId: vehicle.vehicleId,
                        plateNumber: vehicle.vehicle?.plateNumber ?? vehicle.vehicleId ?? null,
                        vehicleTypeName: vehicle.vehicle?.vehicleType?.name ?? undefined,
                        make: vehicle.vehicle?.make ?? undefined,
                        model: vehicle.vehicle?.model ?? undefined,
                        color: vehicle.vehicle?.color ?? undefined,
                        rfidTag: vehicle.rfidTag ?? undefined,
                        isVip: vehicle.isVip
                })) ?? [],
                metrics: {
                        activeParkingSessions: node.parkingSessions?.filter(session => session.isInside).length ?? 0,
                        upcomingInvoiceDate: null,
                        overstayIncidents: 0,
                        lastAuditEvent: null
                },
                notes: [],
                status: computeContractStatus({
                        beginDate: node.beginDate,
                        endDate: node.endDate,
                        isActive: node.isActive
                })
        };
};

export const ContractDetailsQuery = ({
	contractId,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onClose,
	onBack,
	showBackButton
}: ContractDetailsQueryProps) => {
        const { data, loading, error } = useQuery<ContractDetailQueryResponse>(
                CONTRACT_DETAIL_QUERY,
                {
                        variables: { contractId },
                        fetchPolicy: 'cache-and-network',
                        errorPolicy: 'all'
                }
        );

        if (error && !data) {
                return (
                        <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                        Unable to load contract details. Please try again.
                                </AlertDescription>
                        </Alert>
                );
        }

        const contractNode = data?.contract;

        return (
                <DelayedSkeleton
                        isLoading={loading}
                        delay={300}
                        showInitialLoading={false}
                        fallback={<DetailsWithListsSkeleton sections={2} listCount={2} />}
                >
                        {contractNode ? (
                                <ContractDetails
                                        contract={mapNodeToListItem(contractNode)}
                                        showHeader={showHeader}
                                        onEdit={onEdit}
                                        onClone={onClone}
                                        onDelete={onDelete}
                                        onClose={onClose}
                                        onBack={onBack}
                                        showBackButton={showBackButton}
                                />
                        ) : (
                                <Alert>
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                                Contract not found.
                                        </AlertDescription>
                                </Alert>
                        )}
                </DelayedSkeleton>
        );
};
